/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState, useCallback, useRef } from 'react';
import { Review } from '../types/types';
import { axiosInstance } from '../client';
import { BASE_API_URL } from 'src/app/environment';
import { ApiResponse } from '../types/api-types';
import { ReviewCollection } from '../collections';
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  limit as fsLimit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { useAppAuth } from 'src/context/use-app-auth';
import { firestoreDb } from '../client';
import { logAxiosError } from 'src/utils/logger/logs';

type Props = {
  locationId?: string;
  fetchData?: boolean;
  pageSize?: number;
  noReplyOnly?: boolean;
};

type AddReviewProps = {
  reviewText: string;
  reviewId: string;
  locationId: string;
  userId: string;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars

interface ReviewsDataResponse {
  reviews: Review[];
  loading: boolean;
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  addReview: (params: AddReviewProps) => Promise<boolean>;
  revalidate: () => void;
  resetReviews: () => void;
  loadMore: () => Promise<void>;
  hasMore: boolean;
  loadingMore: boolean;
}

const useReviews = ({
  locationId,
  fetchData = true,
  pageSize = 25,
  noReplyOnly = true,
}: Props): ReviewsDataResponse => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [shouldRevalidate, setShouldRevalidate] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const lastDocRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);

  const { user } = useAppAuth();

  const buildQuery = useCallback(
    (after?: QueryDocumentSnapshot<DocumentData> | null) => {
      const collectionPath = `${ReviewCollection}/${locationId}/data/`;
      const colRef = collection(firestoreDb, collectionPath);
      const constraints = [
        ...(noReplyOnly ? [where('hasReply', '==', false)] : []),
        orderBy('updateTime', 'desc'),
        fsLimit(pageSize),
      ];
      if (after) {
        return query(colRef, ...constraints, startAfter(after));
      }
      return query(colRef, ...constraints);
    },
    [locationId, pageSize, noReplyOnly],
  );

  const loadPage = useCallback(
    async (reset = false) => {
      if (!locationId || !fetchData) return;
      reset ? setLoading(true) : setLoadingMore(true);
      try {
        const q = buildQuery(reset ? null : lastDocRef.current);
        const snap = await getDocs(q);
        const docs = snap.docs.map(d => d.data() as Review);
        const last = snap.docs[snap.docs.length - 1] ?? null;
        lastDocRef.current = last;
        setHasMore(snap.size === pageSize);
        setReviews(prev => (reset ? docs : [...prev, ...docs]));
      } catch (e) {
        // Optionally log or set error state
      } finally {
        reset ? setLoading(false) : setLoadingMore(false);
      }
    },
    [buildQuery, fetchData, locationId, pageSize],
  );

  const fetchReviews = useCallback(() => {
    // Initial load or revalidation: reset pagination and fetch first page
    lastDocRef.current = null;
    setHasMore(true);
    setReviews([]);
    return loadPage(true);
  }, [loadPage]);

  useEffect(() => {
    if (fetchData) {
      fetchReviews();
    }
  }, [fetchReviews, shouldRevalidate]);

  // Add review function
  const addReview = async (params: AddReviewProps) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post<ApiResponse>(
        `${BASE_API_URL}/users/addReview`,
        params,
      );
      setLoading(false);
      if (response.data.success) {
        setShouldRevalidate(prev => !prev);
        return true;
      }
    } catch (e) {
      logAxiosError(e);
    }
    setLoading(false);
    return false;
  };

  const revalidate = () => {
    setShouldRevalidate(prev => !prev);
  };

  const loadMore = async () => {
    if (loading || !hasMore) return;
    await loadPage(false);
  };

  const resetReviews = async () => {
    try {
      if (!user?.key) {
        return false;
      }

      const response = await axiosInstance.post<ApiResponse>(
        `${BASE_API_URL}/users/updateLocationReviews`,
        { locationId, userId: user.key },
      );
      setLoading(false);
      if (response.data.success) {
        setShouldRevalidate(prev => !prev);
        return true;
      }
    } catch (e) {
      logAxiosError(e);
    }
    setLoading(false);
    return false;
  };

  return {
    loading,
    reviews,
    addReview,
    revalidate,
    resetReviews,
    loadMore,
    hasMore,
    loadingMore,
  };
};

export { useReviews };
